import { useThree } from "@react-three/fiber";
import { useEffect, useMemo, type JSX } from "react";
import {
  BufferGeometry,
  Color,
  Material,
  Points,
  PointsMaterial,
  Vector3,
} from "three";
import { Line2, LineGeometry, LineMaterial } from "three-stdlib";
import { annotationColorToCss } from "./annotation-colors";
import { cloudOutline, perimeter } from "./annotation-shapes-geometry";
import { AnnotationShapeStyle, OutlineStyle } from "./annotation-shapes-store";

/** Arcs and dashes are sized on the shape, like revision clouds in drawings, so they keep their look at any zoom */
const CLOUD_ARCS_PER_PERIMETER = 28;
const DASHES_PER_PERIMETER = 40;
const GAPS_PER_PERIMETER = 60;

/** How strongly the shape is drawn: a light preview on hover, or full when open or being drawn */
export type ShapeEmphasis = "preview" | "active";

const LINE_WIDTH_PX: Record<ShapeEmphasis, number> = { preview: 2, active: 3 };
const OPACITY: Record<ShapeEmphasis, number> = { preview: 0.6, active: 1 };
const VERTEX_SIZE_PX = 8;

/** Shapes are drawn over the scene, like the other annotations */
const SHAPE_RENDER_ORDER = 10;

/** Shapes are never a click target, so navigation and picking clicks go through them */
function noRaycast(): void {}

/**
 * Disposes the geometry and material of the object when it is replaced or unmounted
 *
 * @param object the three object owned by the component
 */
function useDisposeOnChange(object?: Points | Line2): void {
  useEffect(
    () => () => {
      object?.geometry.dispose();
      if (object?.material instanceof Material) object.material.dispose();
    },
    [object],
  );
}

type AnnotationShapeMeshProps = {
  /** Corners of the shape, in world coordinates */
  points: Vector3[];

  style: AnnotationShapeStyle;

  /** False for a polygon still being drawn */
  isClosed?: boolean;

  emphasis?: ShapeEmphasis;

  /** True while drawing, to show where the corners are */
  showCorners?: boolean;
};

/** @returns the outline of a shape annotation. Never a click target, so navigation clicks go through */
export function AnnotationShapeMesh({
  points,
  style,
  isClosed = true,
  emphasis = "active",
  showCorners = false,
}: AnnotationShapeMeshProps): JSX.Element | null {
  const color = annotationColorToCss(style.color);
  const { outline } = style;

  const line = useMemo(() => {
    if (points.length < 2) return;
    const length = perimeter(points);
    const outlinePoints =
      outline === OutlineStyle.cloud && isClosed && points.length >= 3
        ? cloudOutline(points, length / CLOUD_ARCS_PER_PERIMETER)
        : [...points, ...(isClosed ? [points[0]] : [])];

    const geometry = new LineGeometry();
    geometry.setPositions(outlinePoints.flatMap((p) => p.toArray()));
    const object = new Line2(
      geometry,
      new LineMaterial({
        color: new Color(color).getHex(),
        linewidth: LINE_WIDTH_PX[emphasis],
        opacity: OPACITY[emphasis],
        dashed: outline === OutlineStyle.dashed,
        dashSize: length / DASHES_PER_PERIMETER,
        gapSize: length / GAPS_PER_PERIMETER,
        depthTest: false,
        transparent: true,
      }),
    );
    object.computeLineDistances();
    object.raycast = noRaycast;
    object.renderOrder = SHAPE_RENDER_ORDER + 1;
    return object;
  }, [color, emphasis, isClosed, outline, points]);
  useDisposeOnChange(line);

  const size = useThree((s) => s.size);
  useEffect(() => {
    line?.material.resolution.set(size.width, size.height);
  }, [line, size]);

  const vertices = useMemo(() => {
    if (!showCorners) return;
    const object = new Points(
      new BufferGeometry().setFromPoints(points),
      new PointsMaterial({
        color,
        size: VERTEX_SIZE_PX,
        sizeAttenuation: false,
        depthTest: false,
      }),
    );
    object.raycast = noRaycast;
    object.renderOrder = SHAPE_RENDER_ORDER + 2;
    return object;
  }, [color, points, showCorners]);
  useDisposeOnChange(vertices);

  return (
    <>
      {line && <primitive object={line} />}
      {vertices && <primitive object={vertices} />}
    </>
  );
}
